import { Timestamp, QueryDocumentSnapshot } from 'firebase/firestore';

// Base entity interface
export interface BaseEntity {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: EntityStatus;
}

// Entity status enum
export enum EntityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  ARCHIVED = 'archived',
  DELETED = 'deleted',
}

export enum PropertyStatus {
  AVAILABLE = 'available',
  RESERVED = 'reserved',
  SOLD = 'sold',
}

// User role enum
export enum UserRole {
  ADMIN = 'admin',
  AGENT = 'agent',
  CLIENT = 'client',
  PROMOTEUR = 'promoteur'
}

// Chantier (Project) interface
export interface Chantier extends BaseEntity {
  name: string;
  description: string;
  developerId: string;
  location: GeoPoint;
  heroVideoUrl?: string;
  masterPlanUrl?: string;
  progress: ConstructionProgress;
  units: string[]; // IDs of Properties
  verified: boolean;
  isFeatured?: boolean;
  metadata: ChantierMetadata;
}

export interface ConstructionProgress {
  percentage: number;
  phase: 'structural' | 'finishes' | 'landscaping' | 'delivered';
  timeline: ConstructionTimelineEvent[];
}

export interface ConstructionTimelineEvent {
  date: Timestamp;
  phase: string;
  description: string;
  imageUrl?: string;
}

export interface ChantierMetadata {
  timeToBeach?: string;
  timeToTramway?: string;
  surroundingSchools?: string[];
  isUrbanTitled: boolean; // Titre Foncier vs Melkia
  droneFootageUrl?: string;
}

// Permission enum
export enum Permission {
  READ_PROPERTIES = 'read_properties',
  WRITE_PROPERTIES = 'write_properties',
  DELETE_PROPERTIES = 'delete_properties',
  READ_CLIENTS = 'read_clients',
  WRITE_CLIENTS = 'write_clients',
  DELETE_CLIENTS = 'delete_clients',
  READ_ANALYTICS = 'read_analytics',
  MANAGE_USERS = 'manage_users',
  MANAGE_SYSTEM = 'manage_system'
}

// User settings interface
export interface UserSettings {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  notifications: NotificationSettings;
  dashboard: DashboardSettings;
}

export interface NotificationSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
  propertyMatches: boolean;
  clientUpdates: boolean;
  systemAlerts: boolean;
}

export interface DashboardSettings {
  defaultView: 'grid' | 'list';
  itemsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

// User interface
export interface User extends BaseEntity {
  role: UserRole;
  permissions: Permission[];
  settings: UserSettings;
  profile: UserProfile;
  lastLogin?: Timestamp;
  credits?: number;
  email?: string;
  usage?: {
    totalCalls: number;
    totalTokens: number;
  };
  preferences?: ClientPreferences;
  matchingProfile?: MatchingProfile;
}

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string | null;
  bio?: string;
  company?: string;
  licenseNumber?: string;
  ice?: string;
  favorites?: string[];
  financingStatus?: {
    hasAccordDePrincipe: boolean;
    bankName?: string;
    amount?: number;
    verifiedAt?: Timestamp;
  };
}

// Property metadata interface
export interface PropertyMetadata {
  title: string;
  description: string;
  price: number;
  currency: string;
  propertyType: PropertyType;
  listingType: ListingType;
  bedrooms: number;
  bathrooms: number;
  squareFootage: number;
  lotSize?: number;
  yearBuilt?: number;
  parkingSpaces?: number;
  petFriendly?: boolean;
  furnished?: boolean;
  utilities?: string[];
  amenities?: string[];
  images: string[];
  documents: string[];
  virtualTour?: string;
  videoUrl?: string;
}

export enum PropertyType {
  HOUSE = 'house',
  APARTMENT = 'apartment',
  CONDO = 'condo',
  TOWNHOUSE = 'townhouse',
  STUDIO = 'studio',
  LOFT = 'loft',
  DUPLEX = 'duplex',
  VILLA = 'villa',
  PENTHOUSE = 'penthouse',
  LAND = 'land',
  COMMERCIAL = 'commercial',
  INDUSTRIAL = 'industrial'
}

export enum ListingType {
  SALE = 'sale',
  RENT = 'rent',
  LEASE = 'lease',
  AUCTION = 'auction'
}

// Property features interface
export interface PropertyFeatures {
  heating: string[];
  cooling: string[];
  flooring: string[];
  appliances: string[];
  security: string[];
  outdoor: string[];
  accessibility: string[];
  energyEfficient: boolean;
  smartHome: boolean;
  pool: boolean;
  garden: boolean;
  balcony: boolean;
  fireplace: boolean;
  basement: boolean;
  attic: boolean;
  garage: boolean;
}

// GeoPoint interface for location
export interface GeoPoint {
  latitude: number;
  longitude: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  neighborhood?: string;
  schoolDistrict?: string;
  walkScore?: number;
  transitScore?: number;
}

// Property analytics interface
export interface PropertyAnalytics {
  views: number;
  favorites: number;
  inquiries: number;
  shares: number;
  avgTimeOnPage: number;
  conversionRate: number;
  lastViewed?: Timestamp;
  popularTimes: TimeSlot[];
  demographics: DemographicData;
}

export interface TimeSlot {
  hour: number;
  day: string;
  views: number;
}

export interface DemographicData {
  ageGroups: { [key: string]: number };
  incomeRanges: { [key: string]: number };
  interests: { [key: string]: number };
}

// Property interface
export interface Property extends BaseEntity {
  metadata: PropertyMetadata;
  features: PropertyFeatures;
  location: GeoPoint;
  analytics: PropertyAnalytics;
  agentId: string;
  chantierId?: string;
  ownerId?: string;
  tags: string[];
  searchKeywords?: string[];
  isFeatured: boolean;
  isActive: boolean;
  salesStatus: PropertyStatus;
  listingDate: Timestamp;
  expirationDate?: Timestamp;
}

// Client preferences interface
export interface ClientPreferences {
  priceRange: PriceRange;
  budget?: PriceRange; // Backwards compatibility
  propertyTypes: PropertyType[];
  listingTypes: ListingType[];
  location: LocationPreference;
  features: FeaturePreference;
  timeline: TimelinePreferences;
  financing: FinancingPreferences;
}

export interface PriceRange {
  min: number;
  max: number;
  currency: string;
}

export type BudgetRange = PriceRange;

export interface LocationPreference {
  cities: string[];
  neighborhoods: string[];
  maxDistanceFromWork?: number;
  schoolDistricts?: string[];
  walkScore?: number;
  transitScore?: number;
}

export interface FeaturePreference {
  bedrooms: { min: number; max: number };
  bathrooms: { min: number; max: number };
  squareFootage: { min: number; max: number };
  amenities: string[];
  mustHaves: string[];
  dealBreakers: string[];
}

export interface TimelinePreferences {
  moveInDate?: Date;
  urgency: 'low' | 'medium' | 'high';
  flexibility: 'low' | 'medium' | 'high';
}

export interface FinancingPreferences {
  preApproved: boolean;
  downPayment: number;
  loanType?: string;
  creditScore?: number;
}

// Matching profile interface
export interface MatchingProfile {
  preferences: ClientPreferences;
  behavior: BehaviorProfile;
  communication: CommunicationPreferences;
  priority: PriorityWeight;
}

export interface BehaviorProfile {
  searchFrequency: 'daily' | 'weekly' | 'monthly';
  responseTime: 'immediate' | 'within_hour' | 'within_day' | 'within_week';
  engagementLevel: 'low' | 'medium' | 'high';
  preferredContactMethod: 'email' | 'phone' | 'text' | 'app';
}

export interface CommunicationPreferences {
  language: string;
  timezone: string;
  preferredTimes: string[];
  communicationStyle: 'formal' | 'casual' | 'mixed';
}

export interface PriorityWeight {
  price: number;
  location: number;
  features: number;
  timeline: number;
  quality: number;
}

// Client interaction interface
export interface ClientInteraction {
  id: string;
  type: InteractionType;
  timestamp: Timestamp;
  propertyId?: string;
  agentId: string;
  notes?: string;
  outcome?: InteractionOutcome;
  followUpDate?: Timestamp;
  metadata?: { [key: string]: any };
}

export enum InteractionType {
  VIEW = 'view',
  INQUIRY = 'inquiry',
  FAVORITE = 'favorite',
  SHARE = 'share',
  CALL = 'call',
  EMAIL = 'email',
  MEETING = 'meeting',
  SHOWING = 'showing',
  OFFER = 'offer',
  CONTRACT = 'contract'
}

export enum InteractionOutcome {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
  PENDING = 'pending',
  FOLLOW_UP = 'follow_up'
}

// Client interface
export interface Client extends Omit<BaseEntity, 'status'> {
  preferences: ClientPreferences;
  matchingProfile: MatchingProfile;
  interactions: ClientInteraction[];
  profile: ClientProfile;
  agentId: string;
  source: ClientSource;
  status: ClientStatus;
  tags: string[];
  notes: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ClientProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatar?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  dateOfBirth?: Date;
  occupation?: string;
  income?: number;
  familySize?: number;
  pets?: string[];
  lifestyle?: string[];
}

export enum ClientSource {
  WEBSITE = 'website',
  REFERRAL = 'referral',
  SOCIAL_MEDIA = 'social_media',
  ADVERTISEMENT = 'advertisement',
  OPEN_HOUSE = 'open_house',
  COLD_CALL = 'cold_call',
  PARTNER = 'partner'
}

export enum ClientStatus {
  DISCOVERY = 'discovery',
  RESERVATION = 'reservation',
  BANK_INSTRUCTION = 'bank_instruction',
  COMPROMIS = 'compromis',
  CLOSING = 'closing',
  INACTIVE = 'inactive'
}

// Match score interface
export interface MatchScore {
  overall: number;
  price: number;
  location: number;
  features: number;
  timeline: number;
  quality: number;
  explanation: string;
  strengths: string[];
  weaknesses: string[];
}

// Match status enum
export enum MatchStatus {
  PENDING = 'pending',
  SENT = 'sent',
  VIEWED = 'viewed',
  INTERESTED = 'interested',
  NOT_INTERESTED = 'not_interested',
  SCHEDULED = 'scheduled',
  EXPIRED = 'expired'
}

// Match analytics interface
export interface MatchAnalytics {
  sentAt?: Timestamp;
  viewedAt?: Timestamp;
  respondedAt?: Timestamp;
  responseTime?: number;
  clickThroughRate: number;
  conversionRate: number;
  feedback?: MatchFeedback;
}

export interface MatchFeedback {
  rating: number;
  comments?: string;
  helpful: boolean;
  accurate: boolean;
  relevant: boolean;
}

// Match interface
export interface Match extends Omit<BaseEntity, 'status'> {
  clientId: string;
  propertyId: string;
  agentId: string;
  score: MatchScore;
  status: MatchStatus;
  analytics: MatchAnalytics;
  sentAt?: Timestamp;
  expiresAt?: Timestamp;
}

// Notification interface
export interface Notification extends BaseEntity {
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: { [key: string]: any };
  read: boolean;
  readAt?: Timestamp;
  actionUrl?: string;
  priority: NotificationPriority;
  category: NotificationCategory;
}

export enum NotificationType {
  PROPERTY_MATCH = 'property_match',
  CLIENT_UPDATE = 'client_update',
  SYSTEM_ALERT = 'system_alert',
  REMINDER = 'reminder',
  MESSAGE = 'message',
  APPOINTMENT = 'appointment',
  DOCUMENT = 'document',
  PAYMENT = 'payment'
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum NotificationCategory {
  PROPERTY = 'property',
  CLIENT = 'client',
  SYSTEM = 'system',
  COMMUNICATION = 'communication',
  FINANCIAL = 'financial'
}

// Analytics interfaces
export interface AnalyticsEvent {
  id: string;
  userId: string;
  eventType: string;
  eventData: { [key: string]: any };
  timestamp: Timestamp;
  sessionId: string;
  userAgent: string;
  ipAddress: string;
  referrer?: string;
}

export interface DashboardMetrics {
  totalProperties: number;
  activeProperties: number;
  totalClients: number;
  activeClients: number;
  totalMatches: number;
  successfulMatches: number;
  conversionRate: number;
  revenue: number;
  monthlyGrowth: number;
  topPerformingProperties: Property[];
  recentActivity: ActivityItem[];
}

export interface ActivityItem {
  id: string;
  type: string;
  description: string;
  timestamp: Timestamp;
  userId: string;
  metadata?: { [key: string]: any };
}

// API Response interfaces
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: PaginationInfo;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  nextCursor?: any; // QueryDocumentSnapshot or string
  prevCursor?: any; // QueryDocumentSnapshot or string
}

// Form interfaces
export interface PropertyFormData {
  metadata: Omit<PropertyMetadata, 'images' | 'documents'> & {
    images?: string[];
    documents?: string[];
  };
  features: PropertyFeatures;
  location: GeoPoint;
  tags: string[];
  isFeatured: boolean;
  isActive: boolean;
  listingDate: Date;
  expirationDate?: Date;
}

export interface ClientFormData {
  profile: ClientProfile;
  preferences: ClientPreferences;
  matchingProfile: Omit<MatchingProfile, 'preferences'>;
  source: ClientSource;
  status: ClientStatus;
  tags: string[];
  notes: string;
}

// Search and filter interfaces
export interface PropertySearchFilters {
  propertyTypes?: PropertyType[];
  listingTypes?: ListingType[];
  priceRange?: { min: number; max: number };
  bedrooms?: { min: number; max: number };
  bathrooms?: { min: number; max: number };
  squareFootage?: { min: number; max: number };
  location?: {
    city?: string;
    state?: string;
    zipCode?: string;
    radius?: number;
  };
  features?: string[];
  amenities?: string[];
  isActive?: boolean;
  isFeatured?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
  sortBy?: 'price' | 'date' | 'relevance';
  sortOrder?: 'asc' | 'desc';
  searchTerm?: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PaginationOptions {
  page?: number;
  limit: number;
  cursor?: QueryDocumentSnapshot;
}

export interface ListPropertiesOptions {
  filters?: PropertySearchFilters;
  sort?: SortOptions | SortOptions[];
  pagination?: PaginationOptions;
}

export interface ClientSearchFilters {
  status?: ClientStatus[];
  source?: ClientSource[];
  agentId?: string;
  tags?: string[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  searchTerm?: string;
  preferences?: Partial<ClientPreferences>;
}

export type ClientSortField = 'createdAt' | 'updatedAt' | 'profile.firstName' | 'profile.lastName';

export interface ClientSortOptions {
  field: ClientSortField;
  direction: 'asc' | 'desc';
}

export interface ListClientsOptions {
  filters?: ClientSearchFilters;
  sort?: ClientSortOptions;
  pagination?: PaginationOptions;
}

// Error interfaces
export interface AppError {
  code: string;
  message: string;
  details?: { [key: string]: any };
  timestamp: Timestamp;
  userId?: string;
  stack?: string;
}

// Configuration interfaces
export interface AppConfig {
  firebase: FirebaseConfig;
  features: FeatureFlags;
  ui: UIConfig;
  analytics: AnalyticsConfig;
}

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface FeatureFlags {
  enableMatching: boolean;
  enableAnalytics: boolean;
  enableNotifications: boolean;
  enablePayments: boolean;
  enableVirtualTours: boolean;
}

export interface UIConfig {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  dateFormat: string;
  currency: string;
  timezone: string;
}

export interface AnalyticsConfig {
  trackingId: string;
  enabled: boolean;
  debug: boolean;
  events: string[];
}
